import axios from 'axios'
import { useEffect, useState } from 'react'
import { Spinner, Table } from 'react-bootstrap'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { getTranslation } from '../services/translations'

const number = value => Number(value || 0).toFixed(1)
const matchUrl = matchId =>
  `https://hattrick.org/goto.ashx?path=/Club/Matches/Match.aspx?matchID=${matchId}`

const isSurprisingResult = match => {
  const homeWins = Number(match?.homeWins)
  const draws = Number(match?.draws)
  const awayWins = Number(match?.awayWins)
  const homeGoals = Number(match?.homeGoals)
  const awayGoals = Number(match?.awayGoals)
  if (![homeWins, draws, awayWins, homeGoals, awayGoals].every(Number.isFinite)) return false
  const actualResult = homeGoals > awayGoals ? 'home' : awayGoals > homeGoals ? 'away' : 'draw'
  return (homeWins > 50 && actualResult !== 'home') ||
    (awayWins > 50 && actualResult !== 'away') ||
    (draws > 50 && actualResult !== 'draw')
}

const LeagueSimulationResults = () => {
  const { leagueId, tournamentId } = useParams()
  const simulatorId = tournamentId || leagueId
  const user = useSelector(state => state.user)
  const lang = user?.language || 'fi'
  const t = key => getTranslation(lang, key)
  const [data, setData] = useState(null)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!user?.oauth_token) return
    const params = new URLSearchParams(window.location.search)
    const season = params.get('season')
    const nationalTournament = Boolean(tournamentId)
    const groupId = params.get('groupId')
    const phase = params.get('phase')
    const seasonParam = season ? `&season=${season}` : ''
    const nationalTournamentParams = nationalTournament
      ? `&nationalTournament=true&groupId=${encodeURIComponent(groupId || '')}&phase=${encodeURIComponent(phase || '')}`
      : ''
    axios
      .get(`/api/simulator/league/${user.oauth_token}?leagueId=${simulatorId}${seasonParam}${nationalTournamentParams}`)
      .then(response => setData(response.data))
      .catch(() => setError(t('leagueSimulationFailed')))
  }, [simulatorId, tournamentId, user?.oauth_token, lang])

  if (error) return <div className="alert alert-danger">{error}</div>
  if (!data) return <div className="league-simulation-page league-simulation-loading"><Spinner animation="border" /> {t('leagueSimulationLoading')}</div>

  const leagueTitle = data.isNationalTournament ? data.leagueName : (
    <a href={`https://hattrick.org/goto.ashx?path=/World/Series/?LeagueLevelUnitID=${data.leagueLevelUnitId}`} target="_blank" rel="noreferrer">{data.leagueName}</a>
  )

  return (
    <div className="simulator-page league-simulation-page">
      <h2>{leagueTitle} – {t('season')} {data.season}</h2>
      <div className="league-simulation-card">
        <h4>{t('leagueTable')}</h4>
        <div className="league-simulation-table-wrap">
          <Table className="league-simulation-table" hover size="sm">
            <thead><tr><th>{t('team')}</th><th>{t('leaguePoints')}</th><th>{t('expectedPointsShort')}</th><th>+/-</th><th>{t('goalsForShort')}</th><th>{t('goalsAgainstShort')}</th><th>{t('goalDifferenceShort')}</th><th>{t('expectedGoalsForShort')}</th><th>{t('expectedGoalsAgainstShort')}</th><th>{t('expectedGoalDifferenceShort')}</th><th>{t('goalDifferenceVsExpectedShort')}</th></tr></thead>
            <tbody>{data.table.map(row => <tr key={row.teamId}><td>{row.teamName}</td><td>{row.actualPoints}</td><td>{number(row.expectedPoints)}</td><td>{number(row.actualPoints - row.expectedPoints)}</td><td>{row.goalsFor}</td><td>{row.goalsAgainst}</td><td>{row.goalDifference}</td><td>{number(row.expectedGoalsFor)}</td><td>{number(row.expectedGoalsAgainst)}</td><td>{number(row.expectedGoalDifference)}</td><td>{number(row.goalDifferenceVsExpected)}</td></tr>)}</tbody>
          </Table>
        </div>
      </div>
      <div className="league-simulation-card">
        <h4>{t('matches')}</h4>
        <div className="league-simulation-table-wrap">
          <Table className="league-simulation-table league-matches-table" hover size="sm">
            <thead><tr><th>{t('round')}</th><th>{t('home')}</th><th>{t('away')}</th><th>{t('result')}</th><th>{t('simulation')}</th><th>{t('expectedHomeShort')}</th><th>{t('expectedDrawShort')}</th><th>{t('expectedAwayShort')}</th><th>{t('expectedGoalsHomeShort')}</th><th>{t('expectedGoalsAwayShort')}</th></tr></thead>
            <tbody>{data.matches.map(match => <tr key={match.matchId} className={isSurprisingResult(match) ? 'league-match-surprise' : ''}><td>{match.matchRound}</td><td className="league-team-cell"><a href={`https://hattrick.org/goto.ashx?path=/Club/?TeamID=${match.homeTeamId}`} target="_blank" rel="noreferrer">{match.homeTeamName}</a></td><td className="league-team-cell"><a href={`https://hattrick.org/goto.ashx?path=/Club/?TeamID=${match.awayTeamId}`} target="_blank" rel="noreferrer">{match.awayTeamName}</a></td><td><a href={matchUrl(match.matchId)} target="_blank" rel="noreferrer">{match.homeGoals}-{match.awayGoals}</a></td><td><a href={`/simulator/${match.matchId}`} target="_blank" rel="noreferrer" title={t('simulateMatch')}>🎲</a></td><td>{number(match.homeWins)}</td><td>{number(match.draws)}</td><td>{number(match.awayWins)}</td><td>{number(match.avgHomeGoals)}</td><td>{number(match.avgAwayGoals)}</td></tr>)}</tbody>
          </Table>
        </div>
      </div>
    </div>
  )
}

export default LeagueSimulationResults
